import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getOnSiteAssets } from '../../../models/assetsModel';
import { useHasAnyPermission } from '../../../utils/permissions';
import Button from '../../../components/common/Button';

function OnSiteAssets() {
  const navigate = useNavigate();
  const canViewAssets = useHasAnyPermission(['View Assets']);
  
  const [data, setData] = useState({
    assets: [],
    rows: [] // sites
  });
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchOnSiteAssets = useCallback(async () => {
    setLoading(true);
    try {
      const result = await getOnSiteAssets();
      

      

      
      setData(result);
      // Filter sites based on search term
      let filteredSites = result.rows || [];
      if (searchTerm) {
        filteredSites = filteredSites.filter(site => 
          site.site_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      setTotalCount(filteredSites.length);
    } catch (error) {
      console.error('Error fetching on-site assets:', error);
      toast.error('Failed to load on-site assets');
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    fetchOnSiteAssets();
  }, [fetchOnSiteAssets]);

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  // Filter and paginate data
  const getFilteredAndPaginatedSites = () => {
    let filteredSites = data.rows || [];
    if (searchTerm) {
      filteredSites = filteredSites.filter(site => 
        site.site_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredSites.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const displayedSites = getFilteredAndPaginatedSites();

  const handleManageAssets = (siteId) => {
    navigate(`/dashboard/assets/manage-site/${siteId}`);
  };

  // Check permissions after all hooks are defined
  if (!canViewAssets) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-lg font-semibold mb-2">Access Denied</div>
          <div className="text-gray-600">You do not have permission to view assets.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 py-1">
        {/* Professional Header Card - Following COMPONENT_DESIGN_PATTERNS.md */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4">
          <div className="flex items-start justify-between">
            {/* Left: Icon, Title, Description */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" 
                   style={{ backgroundColor: 'rgba(0, 135, 212, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#0087D4" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">On-Site Assets</h1>
                <p className="text-xs text-gray-500 mb-1">Manage asset assignments to different sites and locations</p>
                <div className="flex items-center mt-1">
                <span className="text-xs font-medium" style={{ color: '#0087D4' }}>
                  {totalCount ? `Showing ${Math.min(pageSize, totalCount)} of ${totalCount} sites` : '0 sites'}
                  {searchTerm && (
                    <span className="ml-1 text-blue-600">
                      (filtered)
                    </span>
                  )}
                </span>
              </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: All Controls on Right */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Clear Filters Button (only show if search is active) */}
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                  >
                    Clear Search
                  </button>
                )}
                
                {/* Refresh Button */}
                <button 
                  onClick={() => fetchOnSiteAssets()} 
                  className="inline-flex items-center px-2 py-1.5 border border-gray-300 shadow-sm text-sm rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500 flex-shrink-0"
                  title="Refresh"
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke={loading ? '#0087D4' : 'currentColor'} style={{ color: loading ? '#0087D4' : 'currentColor' }}>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>


              </div>
              
              {/* Search Box in Row 2 */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={handleSearch}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search sites..."
                />
              </div>
            </div>
          </div>
        </div>



      {/* Site Assets Table */}
      <div className="bg-white overflow-hidden rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full divide-y divide-gray-200">
              <thead style={{ backgroundColor: 'rgba(0, 135, 212, 0.08)' }}>
                <tr>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Site Name
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Assets Assigned
                  </th>
                  <th scope="col" className="px-3 py-2 text-left text-xs font-semibold uppercase" style={{ color: '#0087D4' }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading && (
                  <tr>
                    <td colSpan="3" className="px-3 py-4 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <svg className="animate-spin h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-sm">Loading...</span>
                      </div>
                    </td>
                  </tr>
                )}
                
                {!loading && (!data.rows || data.rows.length === 0) && (
                  <tr>
                    <td colSpan="3" className="px-3 py-6 text-center">
                      <p className="text-gray-600">No sites found</p>
                    </td>
                  </tr>
                )}
                
                {!loading && displayedSites.map(site => {
                  const siteAssets = data.assets?.filter(asset => asset.asset_site === site.site_id) || [];
                  return (
                    <tr key={site.site_id} className="hover:bg-blue-50">
                      <td className="px-3 py-2 text-sm font-medium text-gray-900">
                        {site.site_name}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        {siteAssets.length} assets
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleManageAssets(site.site_id)}
                        >
                          Manage Assets
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        </div>
        
        {/* Enhanced Pagination */}
        {totalCount > 0 && (
          <div className="bg-gray-50 px-3 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
            <div className="flex items-center gap-4">
              <div className="text-gray-500">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
              </div>
              
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Show:</span>
                <select
                  value={pageSize}
                  onChange={handlePageSizeChange}
                  className="border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  {[10, 20, 50, 100].map(size => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
                <span className="text-gray-500">per page</span>
              </div>
            </div>
            
            <div className="flex items-center space-x-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                First
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Previous
              </button>
              
              {/* Page Numbers */}
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers = [];
                  
                  // Show max 5 page numbers
                  let startPage = Math.max(1, currentPage - 2);
                  let endPage = Math.min(totalPages, startPage + 4);
                  
                  // Adjust start if we're near the end
                  if (endPage - startPage < 4) {
                    startPage = Math.max(1, endPage - 4);
                  }
                  
                  for (let i = startPage; i <= endPage; i++) {
                    pageNumbers.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        className={`px-3 py-1.5 border rounded text-xs font-medium ${
                          i === currentPage
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-500 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }
                  
                  return pageNumbers;
                })()}
              </div>
              
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-40 rounded text-xs font-medium"
              >
                Last
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default OnSiteAssets;
